import React, { useState } from 'react';
import { Helmet } from 'react-helmet-async';
import { Phone, Mail, Clock, MapPin, Send, CheckCircle } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import Highlighter from '../components/Highlighter';
import LocalBusinessSchema from '../components/LocalBusinessSchema';
import axios from 'axios';
import '../styles/Clockwork.css';
import '../styles/Contact.css';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;

const About = () => {
  const [formData, setFormData] = useState({
    firstName: '',
    lastName: '',
    businessName: '',
    phone: '',
    email: '',
    needType: '',
    message: ''
  });
  
  const [status, setStatus] = useState({ type: '', message: '' });
  const [isSubmitting, setIsSubmitting] = useState(false);

  const handleChange = (e) => {
    setFormData({
      ...formData,
      [e.target.name]: e.target.value
    });
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setIsSubmitting(true);
    setStatus({ type: '', message: '' });

    try {
      // Map form data to match backend expectations
      const submitData = {
        firstName: formData.firstName,
        lastName: formData.lastName,
        agencyName: formData.businessName, // Backend expects agencyName
        phone: formData.phone,
        email: formData.email,
        message: `${formData.needType ? `Need: ${formData.needType}\n\n` : ''}${formData.message}`
      };

      const response = await axios.post(`${BACKEND_URL}/api/contact`, submitData);
      
      setStatus({
        type: 'success',
        message: 'Thank you! We will be in touch soon.'
      });
      
      // Reset form
      setFormData({
        firstName: '',
        lastName: '',
        businessName: '',
        phone: '',
        email: '',
        needType: '',
        message: ''
      });
    } catch (error) {
      setStatus({
        type: 'error',
        message: 'Something went wrong. Please call us at 801.509.7884 or email mail@xerowrite.com'
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div className="clockwork-app">
      <LocalBusinessSchema />
      <Helmet>
        <title>About Xerowrite | Direct Mail Postcards That Get Results</title>
        <meta name="description" content="Xerowrite has mailed many millions of handwritten-style postcards with fluorescent pink highlighter for businesses across the United States. Based in St. George, Utah." />
        <link rel="canonical" href="https://xerowrite.com/about" />
        
        <meta property="og:type" content="website" />
        <meta property="og:url" content="https://xerowrite.com/about" />
        <meta property="og:title" content="About Xerowrite | Direct Mail That Gets Results" />
        <meta property="og:description" content="Xerowrite has mailed many millions of handwritten-style postcards with fluorescent pink highlighter for businesses across the United States." />
      </Helmet>
      <Header />
      
      <main>
        {/* ABOUT SECTION */}
        <section className="section">
          <div className="container-narrow">
            <h1 className="page-title">We&apos;ve Been Doing This for Years. The Numbers Back It Up.</h1>
            
            <p className="section-subtitle" style={{marginTop: '2rem'}}>
              Xerowrite produces the highest-performing direct mail postcard on the market. We&apos;ve mailed many millions of pieces for businesses across the United States, and our clients come back because the results are real and repeatable.
            </p>
            
            <p className="section-subtitle" style={{marginTop: '1.5rem'}}>
              Our product is built around one exclusive advantage: a variable data <Highlighter>fluorescent pink highlighter</Highlighter> that no other supplier can offer. Paired with handwritten-style personalization, it creates a postcard that looks like it was written specifically for the person holding it, because in every meaningful way, it was.
            </p>
            
            <p className="section-subtitle" style={{marginTop: '1.5rem'}}>
              We&apos;re based in St. George, Utah. We serve businesses of every size, in every industry, across all 50 states. Orders go into production the same day and hit the post office the next business day.
            </p>
            
            <p className="section-subtitle" style={{marginTop: '1.5rem'}}>
              We don&apos;t try to be everything to everyone. We do one thing, the highest-performing direct mail postcard, and we do it better than anyone else.
            </p>
          </div>
        </section>

        {/* CONTACT SECTION */}
        <section className="section section-gray">
          <div className="container-narrow">
            <h2 className="section-title" style={{textAlign: 'center', marginBottom: '3rem'}}>Get in Touch</h2>
          </div>

          <div className="container">
            <div className="contact-layout">
              {/* CONTACT INFO */}
              <div className="contact-info">
                <h3 className="contact-info-title">Contact Details</h3>
                
                <div className="contact-info-items">
                  <a href="tel:8015097884" className="contact-info-item">
                    <Phone size={24} />
                    <div>
                      <div className="contact-label">Phone</div>
                      <div className="contact-value">801.509.7884</div>
                    </div>
                  </a>
                  
                  <a href="mailto:mail@xerowrite.com" className="contact-info-item">
                    <Mail size={24} />
                    <div>
                      <div className="contact-label">Email</div>
                      <div className="contact-value">mail@xerowrite.com</div>
                    </div>
                  </a>
                  
                  <div className="contact-info-item">
                    <Clock size={24} />
                    <div>
                      <div className="contact-label">Hours</div>
                      <div className="contact-value">Monday–Friday, 9:00am to 5:30pm MT</div>
                    </div>
                  </div>
                  
                  <div className="contact-info-item">
                    <MapPin size={24} />
                    <div>
                      <div className="contact-label">Address</div>
                      <div className="contact-value">1575 S River Rd.<br />St. George, UT 84790</div>
                    </div>
                  </div>
                </div>
              </div>

              {/* CONTACT FORM */}
              <div className="contact-form-wrapper">
                <form onSubmit={handleSubmit} className="contact-form">
                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="firstName">First Name *</label>
                      <input
                        type="text"
                        id="firstName"
                        name="firstName"
                        value={formData.firstName}
                        onChange={handleChange}
                        required
                        className="form-input"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="lastName">Last Name *</label>
                      <input
                        type="text"
                        id="lastName"
                        name="lastName"
                        value={formData.lastName}
                        onChange={handleChange}
                        required
                        className="form-input"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="businessName">Business Name *</label>
                    <input
                      type="text"
                      id="businessName"
                      name="businessName"
                      value={formData.businessName}
                      onChange={handleChange}
                      required
                      className="form-input"
                    />
                  </div>

                  <div className="form-row">
                    <div className="form-group">
                      <label htmlFor="email">Email *</label>
                      <input
                        type="email"
                        id="email"
                        name="email"
                        value={formData.email}
                        onChange={handleChange}
                        required
                        className="form-input"
                      />
                    </div>
                    
                    <div className="form-group">
                      <label htmlFor="phone">Phone</label>
                      <input
                        type="tel"
                        id="phone"
                        name="phone"
                        value={formData.phone}
                        onChange={handleChange}
                        className="form-input"
                      />
                    </div>
                  </div>

                  <div className="form-group">
                    <label htmlFor="needType">What do you need?</label>
                    <select
                      id="needType"
                      name="needType"
                      value={formData.needType}
                      onChange={handleChange}
                      className="form-input"
                    >
                      <option value="">Select an option</option>
                      <option value="Order">Order</option>
                      <option value="Samples">Samples</option>
                      <option value="Pricing Info">Pricing Info</option>
                      <option value="Question">Question</option>
                      <option value="Other">Other</option>
                    </select>
                  </div>

                  <div className="form-group">
                    <label htmlFor="message">Message</label>
                    <textarea
                      id="message"
                      name="message"
                      value={formData.message}
                      onChange={handleChange}
                      rows="5"
                      className="form-input"
                    ></textarea>
                  </div>

                  {status.message && (
                    <div className={`form-status ${status.type}`}>
                      {status.type === 'success' ? <CheckCircle size={20} /> : null}
                      {status.message}
                    </div>
                  )}

                  <button type="submit" className="btn btn-primary btn-full" disabled={isSubmitting}>
                    {isSubmitting ? 'Sending...' : (
                      <>
                        <Send size={20} />
                        Send Message
                      </>
                    )}
                  </button>
                </form>
              </div>
            </div>
          </div>
        </section>
      </main>

      <Footer />
    </div>
  );
};

export default About;
